export const supportedInputTypes = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/gif",
  "image/bmp",
  "image/heic",
  "image/heif"
]);

export function isSupportedImage(file: File) {
  return supportedInputTypes.has(file.type) || isHeicFile(file) || file.type.startsWith("image/");
}

export async function readImageDimensions(file: File) {
  if (isHeicFile(file)) {
    const { blob } = await decodeHeicToPng(file);
    return readRasterDimensions(blob);
  }

  return readRasterDimensions(file);
}

export function isHeicFile(file: File | Blob & { name?: string }) {
  const name = "name" in file && file.name ? file.name.toLowerCase() : "";
  const type = file.type.toLowerCase();
  return type === "image/heic" || type === "image/heif" || name.endsWith(".heic") || name.endsWith(".heif");
}

export async function normalizeImageForBrowser(file: File) {
  if (!isHeicFile(file)) return file;

  const { blob } = await decodeHeicToPng(file);
  const name = file.name.replace(/\.(heic|heif)$/i, ".png") || "decoded-heic.png";
  return new File([blob], name, { type: blob.type || "image/png", lastModified: file.lastModified });
}

async function decodeHeicToPng(file: File | Blob) {
  const nativeBlob = await tryNativeDecodeToPng(file);
  if (nativeBlob) return { blob: nativeBlob };

  const decodeOptions = {
    blob: file,
    type: "image/png" as const,
    quality: 1
  };

  try {
    const { heicTo } = await import("heic-to/csp");
    return { blob: await heicTo(decodeOptions) };
  } catch (error) {
    const detail = error instanceof Error ? error.message : "Unknown decoder error";
    throw new Error(`This HEIC image could not be decoded locally. The decoder reported: ${detail}`);
  }
}

async function tryNativeDecodeToPng(file: File | Blob) {
  const image = await tryDecodeWithImage(file);
  if (!image) return undefined;

  try {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d");
    if (!context) return undefined;

    context.drawImage(image, 0, 0);

    return await new Promise<Blob | undefined>((resolve) => {
      canvas.toBlob((blob) => resolve(blob ?? undefined), "image/png");
    });
  } finally {
    URL.revokeObjectURL(image.src);
  }
}

async function tryDecodeWithImage(file: File | Blob) {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = "async";
  img.src = url;

  try {
    await img.decode();
    if (!img.naturalWidth || !img.naturalHeight) {
      URL.revokeObjectURL(url);
      return undefined;
    }
    return img;
  } catch {
    URL.revokeObjectURL(url);
    return undefined;
  }
}

async function readRasterDimensions(file: Blob) {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return { width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}
